import { ErrorHandler, PLATFORM_ID, inject, Service } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * GlobalErrorHandler — the app-wide {@link ErrorHandler}.
 *
 * Two jobs beyond Angular's default (which only logs):
 *
 * 1. **Stale lazy-chunk recovery.** After a new deploy, a client holding the old
 *    `index.html` requests hashed lazy chunks that no longer exist, so the next
 *    lazy route/`@defer` load throws a `ChunkLoadError` / "Failed to fetch
 *    dynamically imported module". We hard-reload once to pull the fresh bundle.
 *    A short-lived `sessionStorage` timestamp guard makes this at-most-once per
 *    10s, so a genuinely persistent failure can never become a reload loop.
 *
 * 2. **Uniform logging.** Everything else is logged with a stable prefix (kept
 *    to `console.error` so it is SSR-safe and dependency-free).
 */
@Service({ autoProvided: false })
export class GlobalErrorHandler implements ErrorHandler {
  private readonly platformId = inject(PLATFORM_ID);

  private static readonly RELOAD_KEY = 'tb:chunk-reload';
  private static readonly RELOAD_COOLDOWN_MS = 10_000;

  handleError(error: unknown): void {
    if (isPlatformBrowser(this.platformId) && this.isChunkLoadError(error)) {
      if (this.tryReloadOnce()) return;
    }
    // eslint-disable-next-line no-console
    console.error('[GlobalErrorHandler]', error);
  }

  private isChunkLoadError(error: unknown): boolean {
    const err = error as { name?: string; message?: string } | null;
    const name = err?.name ?? '';
    const message = err?.message ?? String(error ?? '');
    return (
      name === 'ChunkLoadError' ||
      /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
        message,
      )
    );
  }

  /** Reload at most once per cooldown window; returns true if a reload was triggered. */
  private tryReloadOnce(): boolean {
    try {
      const now = Date.now();
      const last = Number(sessionStorage.getItem(GlobalErrorHandler.RELOAD_KEY) ?? 0);
      if (now - last < GlobalErrorHandler.RELOAD_COOLDOWN_MS) return false;
      sessionStorage.setItem(GlobalErrorHandler.RELOAD_KEY, String(now));
      window.location.reload();
      return true;
    } catch {
      // storage/window unavailable → don't reload, just let the error be logged.
      return false;
    }
  }
}
